/**
 * Cart Upsell ("Extra vibe") — add-to-cart for the compact upsell rows in the cart drawer.
 *
 * Uses event delegation so it survives cart re-renders (morph). After a successful add the
 * button shows the "added" label and stays disabled; the server re-render then marks the row
 * as added for real (snippets/cart-upsell.liquid checks the cart), so the state persists.
 *
 * The cart:update event carries `itemCount` — assets/cart-icon.js reads it to update the header
 * bubble (without it the bubble would be hidden, because it falls back to 0).
 */

const root = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

/**
 * Read the total unit count out of a re-rendered section, falling back to /cart.js.
 * @param {string | undefined} sectionHtml
 * @returns {Promise<number | undefined>}
 */
async function resolveItemCount(sectionHtml) {
  if (sectionHtml) {
    const doc = new DOMParser().parseFromString(sectionHtml, 'text/html');
    const countEl = doc.querySelector('[ref="cartItemCount"]');
    const parsed = countEl ? parseInt(countEl.textContent ?? '', 10) : NaN;
    if (!Number.isNaN(parsed)) return parsed;
  }
  try {
    const res = await fetch(`${root()}cart.js`, { headers: { Accept: 'application/json' } });
    if (!res.ok) return undefined;
    const cart = await res.json();
    return typeof cart.item_count === 'number' ? cart.item_count : undefined;
  } catch {
    return undefined;
  }
}

/**
 * @param {HTMLButtonElement} btn
 * @param {'idle' | 'loading' | 'added'} state
 */
function setButtonState(btn, state) {
  const addText = btn.dataset.addText ?? btn.textContent ?? '';
  const addedText = btn.dataset.addedText ?? '✓';
  btn.classList.toggle('is-loading', state === 'loading');
  btn.classList.toggle('is-added', state === 'added');
  btn.disabled = state !== 'idle';
  btn.textContent = state === 'added' ? addedText : state === 'loading' ? '…' : addText;
}

document.addEventListener('submit', async (e) => {
  const target = e.target;
  if (!(target instanceof HTMLElement)) return;
  const form = target.closest('[data-upsell-form]');
  if (!(form instanceof HTMLFormElement)) return;

  e.preventDefault();

  const btn = form.querySelector('[data-upsell-btn]');
  const variantId = new FormData(form).get('id');

  if (!variantId || (btn instanceof HTMLButtonElement && btn.disabled)) return;

  if (btn instanceof HTMLButtonElement) setButtonState(btn, 'loading');

  try {
    // Ask Shopify to re-render the section that hosts the drawer so the cart HTML comes back with the add.
    const cartComponent = document.querySelector('cart-items-component[data-section-id]');
    const sectionId = cartComponent instanceof HTMLElement ? cartComponent.dataset.sectionId : undefined;

    /** @type {Record<string, unknown>} */
    const requestBody = { id: variantId, quantity: 1 };
    if (sectionId) {
      requestBody.sections = sectionId;
      requestBody.sections_url = window.location.pathname;
    }

    const response = await fetch(`${root()}cart/add.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) throw new Error(`Add to cart failed: ${response.status}`);

    const data = await response.json();
    const sections = data.sections ?? {};
    const itemCount = await resolveItemCount(sectionId ? sections[sectionId] : undefined);

    if (btn instanceof HTMLButtonElement) setButtonState(btn, 'added');

    // component-cart-items.js picks this up and morphs the drawer; cart-icon.js updates the bubble.
    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: data,
          sourceId: 'cart-upsell',
          data: {
            source: 'cart-upsell',
            variantId: String(variantId),
            itemCount,
            sections,
          },
        },
      })
    );
  } catch (err) {
    console.error('[Cart Upsell]', err);
    if (btn instanceof HTMLButtonElement) setButtonState(btn, 'idle');
  }
});

// Multi-variant rows: keep the button in sync with the selected variant's in-cart state.
document.addEventListener('change', (e) => {
  const select = e.target;
  if (!(select instanceof HTMLSelectElement) || !select.classList.contains('cart-upsell__select')) return;
  const form = select.form;
  const btn = form?.querySelector('[data-upsell-btn]');
  if (!(btn instanceof HTMLButtonElement)) return;
  const inCart = select.selectedOptions[0]?.dataset.inCart === 'true';
  setButtonState(btn, inCart ? 'added' : 'idle');
});
